<template>
  <div class="exam-edit-container">
    <div class="exam-edit-content" v-loading="loading">
      <el-form :model="exam" :rules="rules" ref="examForm" class="exam-form" label-width="120px">
        <el-form-item label="测评名称" prop="examName">
          <el-input v-model="exam.examName" placeholder="请输入测评名称"></el-input>
        </el-form-item>

        <el-form-item label="英文简称" prop="examSname">
          <el-input
            v-model="exam.examSname"
            placeholder="如 ocean-60、via-24（字母开头，可含数字与连字符，非空时不可重复）"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="内容标签" prop="tagIds">
          <ContentTagSelect v-model="exam.tagIds" scope="exam" />
          <div class="field-hint">从系统字典多选。题目 JSON 的 question_tag 是计分维度，不要写在这里。</div>
        </el-form-item>

        <el-form-item label="测评简介" prop="profile">
          <el-input
            type="textarea"
            v-model="exam.profile"
            :rows="3"
            placeholder="请输入测评简介"
          ></el-input>
        </el-form-item>

        <el-form-item label="详细说明" prop="description" class="description-editor-item">
          <MonoEdit v-model="exam.description" height="min(56vh, 640px)" />
        </el-form-item>

        <el-form-item label="测评反馈" prop="feedbacks" class="json-editor-item">
          <MonoJsonEdit v-model="exam.feedbacks" field-label="测评反馈" height="min(36vh, 400px)" />
        </el-form-item>

        <el-form-item label="测评总结" prop="summarys" class="json-editor-item">
          <MonoJsonEdit v-model="exam.summarys" field-label="测评总结" height="min(36vh, 400px)" />
        </el-form-item>

        <el-form-item label="题目数量" prop="questionCount">
          <el-input-number v-model="exam.questionCount" :min="1" :max="100"></el-input-number>
        </el-form-item>

        <el-form-item label="限时（分钟）" prop="timeLimit">
          <el-input-number v-model="exam.timeLimit" :min="0" :max="180"></el-input-number>
        </el-form-item>

        <el-form-item label="永久免费" prop="free">
          <el-switch v-model="exam.free" :active-value="1" :inactive-value="0" />
          <div class="field-hint">开启后前台显示「免费」角标</div>
        </el-form-item>

        <el-form-item label="成交价（元）" prop="fee">
          <el-input-number v-model="exam.fee" :min="0" :precision="2" style="width: 100%" />
          <div class="field-hint">实际扣费价格</div>
        </el-form-item>

        <el-form-item label="原价（元）" prop="originFee">
          <el-input-number v-model="exam.originFee" :min="0" :precision="2" style="width: 100%" />
          <div class="field-hint">
            划线价。限时免费：永久免费关 + 成交价 0 + 原价 &gt; 0；限时优惠：0 &lt; 成交价 &lt; 原价
          </div>
        </el-form-item>

        <el-form-item label="封面图片" prop="cover">
          <el-upload
            class="cover-uploader"
            action="/api/v1/upload"
            :headers="uploadHeaders"
            :show-file-list="false"
            :data="uploadData"
            :on-success="handleCoverSuccess"
            :before-upload="beforeCoverUpload"
          >
            <img v-if="exam.cover" :src="exam.cover" class="cover" />
            <el-icon v-else class="cover-uploader-icon"><Plus /></el-icon>
          </el-upload>
        </el-form-item>

        <el-form-item label="缩略图" prop="thumb">
          <el-upload
            class="thumb-uploader"
            action="/api/v1/upload"
            :headers="uploadHeaders"
            :show-file-list="false"
            :data="uploadData"
            :on-success="handleThumbSuccess"
            :before-upload="beforeThumbUpload"
          >
            <img v-if="exam.thumb" :src="exam.thumb" class="thumb" />
            <el-icon v-else class="thumb-uploader-icon"><Plus /></el-icon>
          </el-upload>
        </el-form-item>

        <el-form-item label="买一赠一" prop="oneFree">
          <el-switch v-model="exam.oneFree" :active-value="1" :inactive-value="0" />
        </el-form-item>

        <el-form-item label="评价者ID" prop="evaluatorUserIds">
          <el-input v-model="exam.evaluatorUserIds" placeholder="请输入评价者ID，多个用逗号分隔"></el-input>
        </el-form-item>

        <el-form-item label="被评者ID" prop="evaluateeUserIds">
          <el-input v-model="exam.evaluateeUserIds" placeholder="请输入被评者ID，多个用逗号分隔"></el-input>
        </el-form-item>

        <el-form-item label="统计平均分数查看者" prop="reportAverageUserIds">
          <el-input v-model="exam.reportAverageUserIds" placeholder="请输入可查看统计平均分数者ID，多个用逗号分隔"></el-input>
        </el-form-item>

        <el-form-item label="详细分数查看者" prop="reportDetailUserIds">
          <el-input v-model="exam.reportDetailUserIds" placeholder="请输入可查看详细分数者ID，多个用逗号分隔"></el-input>
        </el-form-item>

        <el-form-item v-if="isCreatorWorkspace" label="审核状态">
          <el-tag :type="exam.auditStatus === 1 ? 'success' : exam.auditStatus === 2 ? 'danger' : 'warning'">
            {{ exam.auditStatus === 1 ? '已通过' : exam.auditStatus === 2 ? '已驳回' : '待审核' }}
          </el-tag>
          <div v-if="exam.auditStatus === 2 && exam.auditMessage" class="field-hint">驳回原因：{{ exam.auditMessage }}</div>
          <div v-else class="field-hint">修改保存后将重新进入待审核，通过后才会出现在公开列表。</div>
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="审核状态" prop="auditStatus">
          <el-select v-model="exam.auditStatus">
            <el-option :value="0" label="未审核"></el-option>
            <el-option :value="1" label="审核通过"></el-option>
            <el-option :value="2" label="审核驳回"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="审核信息" prop="auditMessage">
          <el-input v-model="exam.auditMessage" placeholder="请输入审核信息"></el-input>
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="是否下架" prop="state">
          <el-switch v-model="exam.state" :active-value="0" :inactive-value="1" />
          <div class="field-hint">开启=下架（state=0），关闭=正常上架（state=1）</div>
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="点赞数" prop="praise">
          <el-input-number v-model="exam.praise" :min="0"></el-input-number>
        </el-form-item>

        <el-form-item label="分类ID" prop="categoryId">
          <el-input-number v-model="exam.categoryId" :min="0"></el-input-number>
        </el-form-item>

        <el-form-item label="试题中断提示" prop="stops" class="json-editor-item">
          <MonoJsonEdit v-model="exam.stops" field-label="试题中断提示" height="min(28vh, 320px)" />
        </el-form-item>

        <el-form-item label="题目 Key 集合" prop="questionKeys" class="json-editor-item">
          <MonoJsonEdit v-model="exam.questionKeys" field-label="题目 Key 集合" height="min(28vh, 320px)" />
        </el-form-item>

        <el-form-item label="原始题目" prop="originalQuestions" class="json-editor-item">
          <MonoJsonEdit
            v-model="exam.originalQuestions"
            field-label="原始题目"
            height="min(56vh, 640px)"
          />
          <div class="field-hint">
            保存后由服务端生成答题卷 (questions) 与 max_score；「所有题目」无需编辑。
          </div>
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="审核时间" prop="auditTime">
          <el-date-picker
            v-model="exam.auditTime"
            type="datetime"
            placeholder="选择审核时间"
            :disabled="true"
          />
        </el-form-item>

        <el-form-item label="状态" prop="removed">
          <el-select v-model="exam.removed">
            <el-option :value="0" label="正常"></el-option>
            <el-option :value="1" label="已删除"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="是否热门" prop="isHot">
          <el-switch v-model="exam.isHot" :active-value="1" :inactive-value="0" />
        </el-form-item>

        <el-form-item v-if="!isCreatorWorkspace" label="首页展示" prop="homeDisplay">
          <el-switch v-model="exam.homeDisplay" :active-value="1" :inactive-value="0" />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="submitForm">保存</el-button>
          <el-button @click="cancel">取消</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { validatePhotoUpload, adminUploadFormData, adminUploadHeaders, uploadResponseToUrl } from '@/utils/photoUpload'
import { buildExamSubmitPayload } from '@/utils/examPricing'
import { bumpExamListCache } from '@/utils/listPageCache'
import MonoEdit from './components/MonoEdit.vue'
import MonoJsonEdit from '@/components/MonoJsonEdit.vue'
import { normalizeExamJsonFields } from '@/utils/examJson'
import ContentTagSelect from '@/components/ContentTagSelect.vue'
import { Plus } from '@element-plus/icons-vue'

export default {
  name: 'ExamEditView',
  components: {
    MonoEdit,
    MonoJsonEdit,
    Plus,
    ContentTagSelect
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const isCreatorWorkspace = computed(() => route.meta.creatorExam === true)
    const examForm = ref(null)
    const loading = ref(false)
    const examSnamePattern = /^[a-zA-Z][a-zA-Z0-9]*(-[a-zA-Z0-9]+)*$/

    const exam = ref({
      examName: '',
      examSname: '',
      profile: '',
      description: '',
      feedbacks: '',
      summarys: '',
      questionCount: 1,
      timeLimit: 0,
      free: 0,
      fee: 0,
      originFee: 0,
      cover: '',
      thumb: '',
      questionKeys: '',
      originalQuestions: '',
      oneFree: 0,
      evaluatorUserIds: '',
      evaluateeUserIds: '',
      reportAverageUserIds: '',
      reportDetailUserIds: '',
      auditStatus: 0,
      auditMessage: '',
      auditTime: 0,
      removed: 0,
      state: 1,
      praise: 0,
      categoryId: 0,
      stops: '',
      isHot: 0,
      homeDisplay: 0,
      tagIds: []
    })

    const rules = {
      examName: [
        { required: true, message: '请输入测评名称', trigger: 'blur' },
        { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
      ],
      examSname: [
        {
          validator: (_rule, value, callback) => {
            const s = (value || '').trim()
            if (!s) {
              callback()
              return
            }
            if (!examSnamePattern.test(s)) {
              callback(new Error('格式如 ocean-60、via-24：字母开头，仅字母数字与连字符'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ],
      profile: [
        { required: true, message: '请输入测评简介', trigger: 'blur' },
        { min: 5, max: 200, message: '长度在 5 到 200 个字符', trigger: 'blur' }
      ],
      description: [
        { required: true, message: '请输入详细说明', trigger: 'blur' }
      ],
      questionCount: [
        { required: true, message: '请输入题目数量', trigger: 'blur' }
      ]
    }

    const fetchExam = async () => {
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const response = await axios.get(`/api/v1/exam/${route.params.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          const data = response.data.data
          exam.value = {
            ...data,
            free: Number(data.free) === 1 ? 1 : 0,
            oneFree: Number(data.oneFree) === 1 ? 1 : 0,
            removed: Number(data.removed) === 1 ? 1 : 0,
            state: Number(data.state) === 1 ? 1 : 0,
            isHot: Number(data.isHot) === 1 ? 1 : 0,
            homeDisplay: Number(data.homeDisplay) === 1 ? 1 : 0,
            fee: (Number(data.fee) || 0) / 100,
            originFee: (Number(data.originFee) || 0) / 100,
            tagIds: (data.tagIds || []).map((id) => Number(id)).filter((id) => id > 0)
          }
        } else {
          ElMessage.error(response.data.message || '获取测评信息失败')
        }
      } catch (error) {
        console.error('Error fetching exam:', error)
        ElMessage.error('获取测评信息失败')
      } finally {
        loading.value = false
      }
    }

    const submitForm = async () => {
      if (!examForm.value) return
      
      await examForm.value.validate(async (valid) => {
        if (valid) {
          try {
            loading.value = true
            const token = localStorage.getItem('jwt_token')
            const normalized = normalizeExamJsonFields(exam.value)
            const postData = buildExamSubmitPayload(normalized, { forUpdate: true, feeInYuan: true })
            const response = await axios.post('/api/v1/exam/update', postData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            })

            if (response.data.code === 200) {
              ElMessage.success(isCreatorWorkspace.value ? '保存成功，已重新提交审核' : '保存成功')
              bumpExamListCache()
              if (isCreatorWorkspace.value) {
                router.push({ path: '/creator/works', query: { tab: 'exam' } })
              } else {
                router.push(`/admin/exam/${route.params.id}`)
              }
            } else {
              ElMessage.error(response.data.message || '保存失败')
            }
          } catch (error) {
            console.error('Error updating exam:', error)
            ElMessage.error('保存失败')
          } finally {
            loading.value = false
          }
        }
      })
    }

    const cancel = () => {
      if (isCreatorWorkspace.value) {
        router.push({ path: '/creator/works', query: { tab: 'exam' } })
      } else {
        router.push(`/admin/exam/${route.params.id}`)
      }
    }

    const uploadHeaders = computed(() => adminUploadHeaders())
    const uploadData = computed(() => adminUploadFormData('exam'))

    const handleCoverSuccess = (response) => {
      if (response.code === 200) {
        exam.value.cover = uploadResponseToUrl(response)
        ElMessage.success('上传成功')
      } else {
        ElMessage.error(response.message || '上传失败')
      }
    }

    const beforeCoverUpload = (file) => validatePhotoUpload(file, ElMessage.error)

    const handleThumbSuccess = (response) => {
      if (response.code === 200) {
        exam.value.thumb = uploadResponseToUrl(response)
        ElMessage.success('上传成功')
      } else {
        ElMessage.error(response.message || '上传失败')
      }
    }

    const beforeThumbUpload = (file) => validatePhotoUpload(file, ElMessage.error)

    onMounted(() => {
      if (route.params.id) {
        fetchExam()
      }
    })

    return {
      isCreatorWorkspace,
      exam,
      loading,
      rules,
      examForm,
      uploadHeaders,
      uploadData,
      submitForm,
      cancel,
      handleCoverSuccess,
      beforeCoverUpload,
      handleThumbSuccess,
      beforeThumbUpload
    }
  }
}
</script>

<style scoped>
.exam-edit-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.exam-edit-content {
  width: 100%;
  box-sizing: border-box;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.exam-form {
  width: 100%;
}

.exam-form :deep(.el-form-item__content) {
  flex: 1;
  min-width: 0;
}

.description-editor-item :deep(.el-form-item__content),
.json-editor-item :deep(.el-form-item__content) {
  line-height: normal;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 100%;
}

.exam-form :deep(.mono-json-edit),
.exam-form :deep(.mono-edit) {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.field-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.cover-uploader {
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}

.cover-uploader:hover {
  border-color: var(--el-color-primary);
}

.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
  line-height: 178px;
}

.cover {
  width: 178px;
  height: 178px;
  display: block;
  object-fit: cover;
}

.thumb-uploader {
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}

.thumb-uploader:hover {
  border-color: var(--el-color-primary);
}

.thumb-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
  line-height: 178px;
}

.thumb {
  width: 178px;
  height: 178px;
  display: block;
  object-fit: cover;
}
</style> 